import { PortableTextProps } from '@portabletext/react'
import { FormField, FormFieldState } from '../organisms/or-booking-panel'

/**
 * Walks grid/tab/conditional children so every named field has form state.
 * Without this, {@link Form} skips HTML5 validation when `state[field.name]` is missing (nested fields).
 */
export function buildFormFieldInitialState(fields: FormField[], gaFormField: string, gaCookie: string): FormFieldState {
  const state: FormFieldState = {}

  const ensureLeaf = (field: FormField & { name: string }) => {
    const n = field.name
    if (state[n]) return
    if (n === gaFormField) {
      state[n] = { value: gaCookie }
      return
    }
    switch (field.type) {
      case 'checkbox':
        state[n] = { value: false }
        break
      case 'dropdown':
      case 'calendar':
      case 'time-select':
        state[n] = { value: undefined }
        break
      default:
        state[n] = { value: '' }
    }
  }

  const walk = (list: FormField[]) => {
    for (const field of list) {
      switch (field.type) {
        case 'grid':
          walk(field.fields)
          break
        case 'tab':
          if (field.name) {
            state[field.name] = state[field.name] ?? { value: 0 }
          }
          field.tabs.forEach((tab) => walk(tab.content))
          break
        case 'conditional-checkbox':
          if ('name' in field && field.name) {
            ensureLeaf(field as FormField & { name: string })
          }
          walk(field.fields)
          break
        case 'divider':
        case 'submit':
        case 'custom':
          break
        default:
          if ('name' in field && field.name) {
            ensureLeaf(field as FormField & { name: string })
          }
      }
    }
  }

  walk(fields)
  return state
}

/** Per-field error copy from Sanity form field definitions */
export const getErrorMessages = (
  fields: FormField[]
): {
  [key: string]: PortableTextProps['value']
} =>
  fields.reduce((acc, field) => {
    const checker = (field: FormField) =>
      'errorText' in field && field.errorText ? { ...acc, [field.name]: field.errorText } : acc

    if (field.type === 'grid' || field.type === 'conditional-checkbox') {
      return { ...acc, ...getErrorMessages(field.fields) }
    }

    if (field.type === 'tab') {
      return {
        ...acc,
        ...field.tabs.reduce<ReturnType<typeof checker>>(
          (tabAcc, tab) => ({
            ...tabAcc,
            ...getErrorMessages(tab.content),
          }),
          {}
        ),
      }
    }

    return checker(field)
  }, {})

export const listBulletBlock = (text: string, custom?: object[]) => ({
  _type: 'block',
  style: 'normal',
  markDefs: [],
  level: 1,
  listItem: 'bullet',
  children: text
    ? [
        {
          _type: 'span',
          marks: [],
          text,
        },
      ]
    : custom,
})

export const findBlockIdx = (block: { children: { text: string }[] }[], placeholder = '[error]') =>
  block.findIndex((b) => b.children.some((t) => t.text === placeholder))

export const replaceBlock = (block: object[], position: number, toInsert: object[]) => {
  const beginning = block.slice(0, position)
  const end = block.slice(position + 1, block.length)
  return [...beginning, ...toInsert, ...end]
}

export const prepareErrors = (fieldNames: string[], errorBlocks?: { [p: string]: PortableTextProps['value'] }) =>
  fieldNames.flatMap((field) => {
    if (!(errorBlocks && field in errorBlocks && errorBlocks[field])) return []
    return [listBulletBlock('', errorBlocks[field] as object[])]
  })
